/**
 * AppInitializer Component
 *
 * Handles WebSocket initialization during app startup
 * - Registers user with WebSocket after app loads if authenticated
 * - Listens for authentication state changes
 * - Manages device ID cleanup on logout
 *
 * This component should be placed inside both AppProvider and WebSocketProvider
 * to have access to both contexts.
 */

import { useContext, useEffect, useRef } from "react";
import AppContext from "../components/context/context";
import { useWebSocket } from "./WebSocketContext";
import { getOrCreateDeviceId, clearDeviceId } from "../utils/deviceId.util";

/**
 * Initialize WebSocket connection when user is authenticated
 * Checks authentication state on app load and whenever it changes
 */
function AppInitializer() {
  const { isAuthenticated, user } = useContext(AppContext);
  const { registerUser } = useWebSocket();
  const registeredUserIdRef = useRef(null);

  // Watch for authentication changes and register with WebSocket
  // Only register once per unique userId
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userJson = localStorage.getItem("user");

    // User is authenticated if both token and user data exist
    if (isAuthenticated && token && userJson) {
      try {
        const userData = JSON.parse(userJson);
        const userId = userData._id || userData.id;

        // Only register if we have a valid user ID AND haven't registered for this user yet
        if (userId && registeredUserIdRef.current !== userId) {
          // Get or create unique device ID for multi-device support
          const deviceId = getOrCreateDeviceId();

          console.log(`🔌 [AppInit] Registering user: ${userId}`);

          // Register with both userId and deviceId - only once per user
          registerUser(userId, deviceId);
          registeredUserIdRef.current = userId;
        }
      } catch (error) {
        console.error("🔌 [AppInit] Error parsing user data:", error);
      }
    } else if (!isAuthenticated) {
      // User logged out - clear device ID and registered reference
      console.log("🔌 [AppInit] User logged out, clearing device ID");
      clearDeviceId();
      registeredUserIdRef.current = null;
    }
  }, [isAuthenticated, registerUser]);

  // This component doesn't render anything, it just manages initialization
  return null;
}

export default AppInitializer;
